/**
 * UserProfilePage — V2 orchestrator for the User profile.
 *
 * Manages: session/permissions, two polling cycles (basic 600s, reporting 1200s),
 * tab state (basic, reporting, logs), drawers, dialogs, toast.
 *
 * Props: id — User ID (from URL param)
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './user-profile-page.css';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import ProfileBanner from '../../servers/profile/components/ProfileBanner.jsx';
import BasicInfoTab from './components/BasicInfoTab.jsx';
import BasicInfoDrawer from './components/BasicInfoDrawer.jsx';
import ReportingTab from './components/ReportingTab.jsx';
import LogsTab from './components/LogsTab.jsx';
import ResetPasswordDialog from './components/ResetPasswordDialog.jsx';
import DeleteUserDialog from './components/DeleteUserDialog.jsx';
import EventNoteDialog from '../../reports/alert-details/components/EventNoteDialog.jsx';

import {
    getSession, setSession, getCampusId, getUserId, getSessionId,
    getCampusList, updateSessionCampus, getRoleWeight,
} from '../../../services/session.js';
import { iAmHigherAuthority, formatDateIso, getToday, addDaysToDate } from '../../../libs.jsx';
import {
    fetchUserProfile, fetchUserRoles, fetchUserLogs, fetchUserBadges,
    fetchHourChart, fetchWeekChart, fetchReasonSummary, fetchLimitedEvents,
    fetchPreformNotes,
    editUserProfile, resetUserPassword, deleteUser, addEventNote,
} from './api/userProfileApi.js';

const UserProfilePage = ({ id }) => {
    const navigate = useNavigate();
    const userId = id;

    // ======================================================================
    // SESSION & PERMISSIONS
    // ======================================================================
    const session = useMemo(() => getSession(), []);
    const campusList = useMemo(() => getCampusList() || [], []);
    const [campusId, setCampusId] = useState(() => getCampusId());
    const campusConfig = useMemo(() => {
        if (!session?.campusData || !campusId) return null;
        return session.campusData[campusId.toString()] ?? null;
    }, [session, campusId]);
    const tzOffset = campusConfig?.tz_offset;
    const isCloud = Boolean(campusConfig?.is_cloud);
    // Mirrors UsersPage.jsx: tag the edit payload with the campus's grandstream
    // flag so the backend can decide whether to (de)provision a SIP extension.
    const isGrandstream = Boolean(campusConfig?.is_grandstream);
    const myRoleWeight = getRoleWeight();
    const editRights = iAmHigherAuthority(10, myRoleWeight);
    const showAdminTabs = iAmHigherAuthority(80, myRoleWeight);
    const eventNotesActive = Boolean(session?.event_notes_active);
    const eventNotesPreform = Boolean(session?.event_notes_preform);
    const eventNotesFreeform = Boolean(session?.event_notes_freeform);

    // ======================================================================
    // DATA STATE
    // ======================================================================
    const [basicData, setBasicData] = useState(null);
    const [roleData, setRoleData] = useState([]);
    const [logData, setLogData] = useState([]);
    const [badgeData, setBadgeData] = useState([]);
    const [hourData, setHourData] = useState({});
    const [weekData, setWeekData] = useState({});
    const [reasonData, setReasonData] = useState({});
    const [eventData, setEventData] = useState([]);
    const [prenoteData, setPrenoteData] = useState([]);
    const [loading, setLoading] = useState(true);

    // Derived
    const displayName = basicData?.display_name || '';
    const username = basicData?.username || '';
    const userRoleWeight = parseInt(basicData?.user_role_weight || 0, 10);
    const higherRole = iAmHigherAuthority(userRoleWeight, myRoleWeight);
    const userGroups = basicData?.groups || [];

    // ======================================================================
    // TAB STATE
    // ======================================================================
    const [activeTab, setActiveTab] = useState('basic');

    // ======================================================================
    // DRAWER/DIALOG STATE
    // ======================================================================
    const [basicDrawerOpen, setBasicDrawerOpen] = useState(false);
    const [resetPwDialogOpen, setResetPwDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [noteDialogOpen, setNoteDialogOpen] = useState(false);
    const [noteEventIndex, setNoteEventIndex] = useState(null);
    const [saving, setSaving] = useState(false);

    // ======================================================================
    // TOAST
    // ======================================================================
    const [toast, setToast] = useState(null);
    const toastTimerRef = useRef(null);

    const showToast = useCallback((type, message) => {
        clearTimeout(toastTimerRef.current);
        setToast({ type, message });
        toastTimerRef.current = setTimeout(() => setToast(null), 6000);
    }, []);

    useEffect(() => () => clearTimeout(toastTimerRef.current), []);

    // ======================================================================
    // DATA LOADING — Cycle 1: Basic (600s)
    // ======================================================================
    const loadBasicData = useCallback(async () => {
        if (!campusId || !userId) return;
        setLoading(true);
        try {
            const [profile, roles, logs, badges] = await Promise.all([
                fetchUserProfile(campusId, userId),
                fetchUserRoles(campusId),
                showAdminTabs ? fetchUserLogs(userId) : Promise.resolve([]),
                fetchUserBadges(campusId, userId),
            ]);
            setBasicData(Array.isArray(profile) && profile.length > 0 ? profile[0] : null);
            setRoleData(Array.isArray(roles) ? roles : []);
            setLogData(Array.isArray(logs) ? logs : []);
            setBadgeData(Array.isArray(badges) ? badges : []);
        } catch (err) {
            console.error('[UserProfilePage] loadBasicData error:', err);
        } finally {
            setLoading(false);
        }
    }, [campusId, userId, showAdminTabs]);

    useEffect(() => { loadBasicData(); }, [loadBasicData]);

    // Polling: basic cycle
    useEffect(() => {
        const timer = setInterval(loadBasicData, 600000);
        return () => clearInterval(timer);
    }, [loadBasicData]);

    // ======================================================================
    // DATA LOADING — Cycle 2: Reporting (1200s)
    // ======================================================================
    const loadReportingData = useCallback(async () => {
        if (!campusId || !userId) return;
        try {
            const today = getToday();
            const startDate = formatDateIso(addDaysToDate(today, -28));
            const endDate = formatDateIso(today);

            const [hour, week, reason, events, prenotes] = await Promise.all([
                fetchHourChart(campusId, userId),
                fetchWeekChart(campusId, userId),
                fetchReasonSummary(campusId, userId, startDate, endDate),
                fetchLimitedEvents(campusId, userId),
                eventNotesActive ? fetchPreformNotes(campusId) : Promise.resolve([]),
            ]);
            setHourData(hour || {});
            setWeekData(week || {});
            setReasonData(reason || {});
            setEventData(Array.isArray(events) ? events : []);
            setPrenoteData(Array.isArray(prenotes) ? prenotes : []);
        } catch (err) {
            console.error('[UserProfilePage] loadReportingData error:', err);
        }
    }, [campusId, userId, eventNotesActive]);

    useEffect(() => { loadReportingData(); }, [loadReportingData]);

    useEffect(() => {
        const timer = setInterval(loadReportingData, 1200000);
        return () => clearInterval(timer);
    }, [loadReportingData]);

    // ======================================================================
    // HANDLERS
    // ======================================================================

    // Edit basic info
    const handleSaveBasic = useCallback(async (formData) => {
        setSaving(true);
        const payload = {
            user_id: userId,
            display_name: formData.display_name,
            email: formData.email,
            phone: formData.phone,
            user_role_id: formData.user_role_id,
            title: formData.title,
            username,
            campus_id: campusId,
            cognito_id: basicData?.cognito_id,
            sip_extension: basicData?.sip_extension,
            wave_password: basicData?.wave_password,
            is_grandstream: isGrandstream,
            group_ids: userGroups.map((g) => g.group_id),
            modified_by: getUserId(),
        };
        const oldPayload = {
            user_id: userId,
            display_name: displayName,
            email: basicData?.email,
            phone: basicData?.phone,
            user_role_id: basicData?.user_role_id,
            title: basicData?.title,
            username,
            groups: userGroups.map((g) => g.group_id),
            campus_id: campusId,
        };
        const result = await editUserProfile(payload, oldPayload, isCloud, { userId: getUserId(), clientId: getSessionId() });
        setSaving(false);
        if (result[0]) {
            showToast('success', 'User updated successfully.');
            setBasicDrawerOpen(false);
            // Update session if editing yourself
            if (username === session?.username) {
                const s = getSession();
                if (s) {
                    s.email = formData.email.toLowerCase();
                    s.display = formData.display_name;
                    s.title = formData.title;
                    setSession(s);
                }
            }
            loadBasicData();
        } else {
            showToast('error', result[1] || 'Failed to save.');
        }
    }, [userId, username, campusId, basicData, userGroups, displayName, isCloud, isGrandstream, session, showToast, loadBasicData]);

    // Reset password
    const handleResetPassword = useCallback(async (newPassword) => {
        setSaving(true);
        const payload = { user_id: userId, username, campus_id: campusId, cognito_id: basicData?.cognito_id, password: newPassword, modified_by: getUserId() };
        const result = await resetUserPassword(payload, isCloud, { userId: getUserId(), clientId: getSessionId() });
        setSaving(false);
        if (result[0]) {
            showToast('success', 'Password reset successfully.');
            setResetPwDialogOpen(false);
        } else {
            showToast('error', result[1] || 'Failed to reset password.');
        }
    }, [userId, username, campusId, basicData, isCloud, showToast]);

    // Delete user
    const handleDelete = useCallback(async () => {
        setSaving(true);
        // sip_extension + is_grandstream let the backend clean up the user's
        // SIP registration alongside the user record itself. Mirrors the
        // shape of deleteUsers() in src/pages/users/api/usersApi.js.
        const payload = {
            user_id: userId,
            username,
            campus_id: campusId,
            cognito_id: basicData?.cognito_id,
            sip_extension: basicData?.sip_extension,
            is_grandstream: isGrandstream,
            modified_by: getUserId(),
        };
        const result = await deleteUser(payload, isCloud, { userId: getUserId(), clientId: getSessionId() });
        setSaving(false);
        if (result[0]) {
            showToast('success', 'User deleted.');
            navigate('/users/usermanagement');
        } else {
            showToast('error', result[1] || 'Failed to delete user.');
        }
    }, [userId, username, campusId, basicData, isCloud, isGrandstream, showToast, navigate]);

    // Add event note
    const handleAddNote = useCallback(async (eventId, noteText) => {
        setSaving(true);
        const result = await addEventNote(eventId, campusId, noteText, getUserId(), getSessionId());
        setSaving(false);
        if (result[0]) {
            showToast('success', 'Note added.');
            setNoteDialogOpen(false);
            setNoteEventIndex(null);
            loadReportingData();
        } else {
            showToast('error', result[1] || 'Failed to add note.');
        }
    }, [campusId, showToast, loadReportingData]);

    // Campus change
    const handleCampusChange = useCallback((e) => {
        const val = parseInt(e.target.value, 10);
        setCampusId(val);
        updateSessionCampus(val);
    }, []);

    // ======================================================================
    // TAB CONFIG
    // ======================================================================
    const tabs = useMemo(() => {
        const t = [
            { key: 'basic', label: 'Basic Info' },
            { key: 'reporting', label: 'Reporting' },
        ];
        if (showAdminTabs) t.push({ key: 'logs', label: 'User Logs' });
        return t;
    }, [showAdminTabs]);

    // ======================================================================
    // RENDER
    // ======================================================================
    return (
        <>
            <NavigationV2 />
            <div className="nav2-main">
                <div className="dp-page">
                    {/* Toast */}
                    {toast ? (
                        <div className={`dp-toast dp-toast--${toast.type}`} role="status" aria-live="polite">
                            {toast.type === 'success' ? (
                                <svg style={{ width: '16px', height: '16px', flexShrink: 0 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            ) : (
                                <svg style={{ width: '16px', height: '16px', flexShrink: 0 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" /></svg>
                            )}
                            <span>{toast.message}</span>
                            <button onClick={() => setToast(null)} className="dp-toast__close" aria-label="Dismiss">{'\u2715'}</button>
                        </div>
                    ) : null}

                    {/* Page Header */}
                    <header className="dp-page-header">
                        <div className="dp-page-header__inner">
                            <div className="dp-breadcrumb-row">
                                <nav className="dp-breadcrumb">
                                    <Link to="/users/usermanagement" className="dp-breadcrumb__text">Users</Link>
                                    <span className="dp-breadcrumb__separator">/</span>
                                    <span className="dp-breadcrumb__current">{displayName || 'User Profile'}</span>
                                </nav>
                                <div className="dp-campus-row">
                                    <label htmlFor="upp-campus-selector" className="dp-campus-label">Campus:</label>
                                    {campusList.length > 1 ? (
                                        <select id="upp-campus-selector" className="dp-campus-select" value={campusId} onChange={handleCampusChange}>
                                            {campusList.map((c) => (<option key={c.campus_id} value={c.campus_id}>{c.campus_name}</option>))}
                                        </select>
                                    ) : (
                                        <span className="dp-campus-name">{campusConfig?.name || ''}</span>
                                    )}
                                </div>
                            </div>

                            {/* Lifecycle actions */}
                            {editRights && higherRole && basicData ? (
                                <div className="upp-lifecycle-actions">
                                    <button className="dp-btn dp-btn--secondary dp-btn--sm" onClick={() => setResetPwDialogOpen(true)}>
                                        <i className="fe fe-lock" style={{ marginRight: '4px' }} />Reset Password
                                    </button>
                                    <button className="dp-btn dp-btn--danger dp-btn--sm" onClick={() => setDeleteDialogOpen(true)}>
                                        <i className="fe fe-trash-2" style={{ marginRight: '4px' }} />Delete
                                    </button>
                                </div>
                            ) : null}
                        </div>
                    </header>

                    {/* Profile Banner */}
                    <ProfileBanner
                        name={displayName}
                        subtitle={basicData?.user_role_name || ''}
                        metaFields={[
                            { label: 'Username', value: username },
                            { label: 'Email', value: basicData?.email || '—' },
                            { label: 'Phone', value: basicData?.phone || '—' },
                        ]}
                        loading={loading}
                    />

                    {/* Tab Bar */}
                    <div className="pp-tab-bar">
                        {tabs.map((tab) => (
                            <button
                                key={tab.key}
                                className={`pp-tab${activeTab === tab.key ? ' is-active' : ''}`}
                                onClick={() => setActiveTab(tab.key)}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* Tab Content */}
                    <div className="pp-tab-content">
                        {activeTab === 'basic' ? (
                            <BasicInfoTab
                                basicData={basicData}
                                userGroups={userGroups}
                                editRights={editRights}
                                higherRole={higherRole}
                                onEdit={() => setBasicDrawerOpen(true)}
                                isGrandstream={isGrandstream}
                                badges={badgeData}
                                tzOffset={tzOffset}
                            />
                        ) : null}

                        {activeTab === 'reporting' ? (
                            <ReportingTab
                                hourData={hourData}
                                weekData={weekData}
                                reasonData={reasonData}
                                eventData={eventData}
                                eventNotesActive={eventNotesActive}
                                tzOffset={tzOffset}
                                onAddNote={(evt, idx) => { setNoteEventIndex(idx); setNoteDialogOpen(true); }}
                                userId={userId}
                            />
                        ) : null}

                        {activeTab === 'logs' && showAdminTabs ? (
                            <LogsTab logData={logData} tzOffset={tzOffset} />
                        ) : null}
                    </div>

                    {/* Edit Drawer */}
                    <BasicInfoDrawer
                        open={basicDrawerOpen}
                        onClose={() => setBasicDrawerOpen(false)}
                        onSave={handleSaveBasic}
                        saving={saving}
                        basicData={basicData}
                        roles={roleData}
                        myRoleWeight={myRoleWeight}
                    />

                    {/* Reset Password Dialog */}
                    {resetPwDialogOpen ? (
                        <ResetPasswordDialog
                            userName={displayName}
                            isHighAuthority={iAmHigherAuthority(80, userRoleWeight)}
                            onClose={() => setResetPwDialogOpen(false)}
                            onConfirm={handleResetPassword}
                            saving={saving}
                        />
                    ) : null}

                    {/* Delete Dialog */}
                    {deleteDialogOpen ? (
                        <DeleteUserDialog
                            userName={displayName}
                            onClose={() => setDeleteDialogOpen(false)}
                            onConfirm={handleDelete}
                            saving={saving}
                        />
                    ) : null}

                    {/* Event Note Dialog */}
                    {noteDialogOpen && noteEventIndex !== null ? (
                        <EventNoteDialog
                            event={eventData[noteEventIndex]}
                            prenoteData={prenoteData}
                            eventNotesPreform={eventNotesPreform}
                            eventNotesFreeform={eventNotesFreeform}
                            tzOffset={tzOffset}
                            onClose={() => { setNoteDialogOpen(false); setNoteEventIndex(null); }}
                            onSave={handleAddNote}
                            saving={saving}
                        />
                    ) : null}
                </div>
            </div>
        </>
    );
};

export default UserProfilePage;
